import {
  ChartableSurfaceResolver,
  ChartedItemScopeResolver,
  ChartedItemTotalCalculator,
  ChartedMultiStepTreatment,
  ChartedSurface,
  ChartedTreatment,
  ChartedTreatmentUpdater,
  FeeScheduleManager,
  type ISurfaceScopeRefPair,
  PricedServiceCodeEntry,
  PricedServiceCodeGroup,
  stafferToNamedDoc,
  TreatmentConfiguration,
  TreatmentStep,
  upsertTreatmentsToStep,
} from '@principle-theorem/principle-core';
import {
  ChartableSurface,
  type IChartedMultiStepTreatment,
  type IChartedMultiStepTreatmentStep,
  type IChartedRef,
  type IChartedSurface,
  type IChartedTreatment,
  type IFeeSchedule,
  type IMultiTreatmentConfiguration,
  type IMultiTreatmentPackage,
  type IPricedServiceCodeEntry,
  type IStaffer,
  type ITreatmentConfiguration,
  type ITreatmentStep,
  type ITreatmentStepConfiguration,
  ServiceCodeGroupType,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  getDoc,
  type INamedDocument,
  reduceToSingleArrayFn,
  snapshot,
  toNamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { first, flatten, isEqual, sumBy } from 'lodash';
import { type Observable } from 'rxjs';
import { v4 as uuid } from 'uuid';
import {
  type ITreatmentAndSurface,
  type ITreatmentStepWithSurfaces,
} from './multi-treatment-selector.store';

export class MultiTreatmentBuilder {
  constructor(private _chartingAs$: Observable<WithRef<IStaffer>>) {}

  applyPackage(
    treatmentPackage: IMultiTreatmentPackage,
    stepsWithSurfaces: ITreatmentStepWithSurfaces[],
    feeSchedule: WithRef<IFeeSchedule>
  ): ITreatmentStepWithSurfaces[] {
    const calc = new ChartedItemTotalCalculator();

    return stepsWithSurfaces.map((step, index) => {
      const packageStep = treatmentPackage.steps[index];
      if (!packageStep) {
        return step;
      }

      const treatments = step.treatments.map((treatment, treatmentIndex) => {
        const packageTreatment = packageStep.treatments[treatmentIndex];
        if (!packageTreatment) {
          return treatment;
        }

        const pricedServiceCodes = treatment.pricedServiceCodes.map(
          (serviceCode, serviceCodeIndex) =>
            ChartedTreatment.patchPackagePrice(
              packageTreatment.priceOverrides,
              serviceCode,
              serviceCodeIndex,
              treatment.pricedServiceCodes
            )
        );

        const serviceCodeGroups = treatment.serviceCodeGroups.map((group) => ({
          ...group,
          serviceCodes: group.serviceCodes.map((serviceCode, codeIndex) =>
            ChartedTreatment.patchPackagePrice(
              packageTreatment.priceOverrides,
              serviceCode,
              codeIndex,
              group.serviceCodes
            )
          ),
        }));

        const serviceCodeSmartGroups = treatment.serviceCodeSmartGroups.map(
          (group) => ({
            ...group,
            serviceCodes: group.serviceCodes.map((serviceCode, codeIndex) =>
              ChartedTreatment.patchPackagePrice(
                packageTreatment.priceOverrides,
                serviceCode,
                codeIndex,
                group.serviceCodes
              )
            ),
          })
        );

        const chartedTreatment = ChartedTreatment.init({
          config: treatment.treatment,
          feeSchedule,
          scopeRef: {
            scope: ChartableSurface.Unscoped,
          },
          basePriceOverride: packageTreatment.basePriceOverride,
          serviceCodes: pricedServiceCodes,
          serviceCodeGroups,
          serviceCodeSmartGroups,
        });

        const treatmentPrice = calc.treatment(chartedTreatment);
        return {
          ...treatment,
          pricedServiceCodes,
          serviceCodeGroups,
          serviceCodeSmartGroups,
          price: treatment.disabled
            ? treatmentPrice
            : treatmentPrice * treatment.chartedSurfaces.length,
          basePriceOverride: packageTreatment.basePriceOverride,
        };
      });

      return {
        ...step,
        treatments,
        price: sumBy(treatments, 'price'),
      };
    });
  }

  async getChartedMultiTreatment(
    configuration: WithRef<IMultiTreatmentConfiguration>,
    stepsWithSurfaces: ITreatmentStepWithSurfaces[],
    feeSchedule: WithRef<IFeeSchedule>,
    selectedPackage?: IMultiTreatmentPackage
  ): Promise<IChartedMultiStepTreatment> {
    const multiTreatment = ChartedMultiStepTreatment.init({
      steps: [],
      config: toNamedDocument(configuration),
      feeSchedule: toNamedDocument(feeSchedule),
      scopeRef: { scope: ChartableSurface.WholeMouth },
    });

    const steps = await asyncForEach(stepsWithSurfaces, (step) =>
      this.stepToChartedStep(step, feeSchedule)
    );

    const combinedSurfaces = steps
      .map((step) =>
        step.treatments
          .map((treatment) => treatment.chartedSurfaces)
          .reduce(reduceToSingleArrayFn, [])
      )
      .reduce(reduceToSingleArrayFn, []);

    return {
      ...multiTreatment,
      steps,
      chartedSurfaces: combinedSurfaces,
      treatmentPackageId: selectedPackage?.uid,
    };
  }

  async initialiseTreatmentStepWithSurfaces(
    step: ITreatmentStepConfiguration,
    selectedSurfaces: Partial<IChartedRef>[],
    feeSchedule: WithRef<IFeeSchedule>
  ): Promise<ITreatmentStepWithSurfaces> {
    const feeScheduleManager =
      await FeeScheduleManager.createFromSchedule(feeSchedule);
    const treatments = await asyncForEach(step.treatments, async (treatment) =>
      this._getTreatmentAndSurface(
        await getDoc(treatment.ref),
        treatment.quantity,
        feeSchedule,
        selectedSurfaces,
        feeScheduleManager
      )
    );
    return {
      step,
      treatments,
      price: sumBy(treatments, 'price'),
    };
  }

  async stepToChartedStep(
    step: ITreatmentStepWithSurfaces,
    feeSchedule: WithRef<IFeeSchedule>
  ): Promise<IChartedMultiStepTreatmentStep> {
    const treatmentStep = TreatmentStep.init({
      name: step.step.name,
      schedulingRules: step.step.schedulingRules,
    });

    const sharedFeeScheduleManager =
      await FeeScheduleManager.createFromSchedule(feeSchedule);

    const combinedTreatments: IChartedTreatment[] = [];
    for (const treatment of step.treatments) {
      const resolver = new ChartedItemScopeResolver();
      const surfaceScopeRefPairs = resolver.reduceChartedSurfacesToScope(
        treatment.treatment,
        treatment.chartedSurfaces
      );

      if (surfaceScopeRefPairs.length) {
        const chartedTreatments = await this.getChartedTreatments(
          treatmentStep,
          treatment,
          surfaceScopeRefPairs,
          feeSchedule,
          sharedFeeScheduleManager
        );
        combinedTreatments.push(...chartedTreatments);
        continue;
      }

      const newSurfaceScopeRefPairs = [
        {
          surfaces: [],
          scopeRef: {
            scope: resolver.determineScope(
              treatment.treatment,
              treatment.chartedSurfaces
            ),
          },
        },
      ];

      for (let index = 0; index < treatment.quantity; index++) {
        const chartedTreatments = await this.getChartedTreatments(
          treatmentStep,
          treatment,
          newSurfaceScopeRefPairs,
          feeSchedule,
          sharedFeeScheduleManager
        );
        combinedTreatments.push(...chartedTreatments);
      }
    }

    const synced =
      await ChartedTreatmentUpdater.syncPricingRules(combinedTreatments);
    return {
      ...treatmentStep,
      treatments: synced,
      uid: uuid(),
    };
  }

  async getChartedTreatments(
    treatmentStep: ITreatmentStep,
    treatment: ITreatmentAndSurface,
    surfaceScopeRefPairs: ISurfaceScopeRefPair[],
    feeSchedule: WithRef<IFeeSchedule>,
    feeScheduleManager?: FeeScheduleManager
  ): Promise<IChartedTreatment[]> {
    const resolver = new ChartedItemScopeResolver();

    const { treatments } = await upsertTreatmentsToStep(
      treatmentStep,
      treatment.treatment,
      surfaceScopeRefPairs,
      resolver,
      feeSchedule,
      undefined,
      true,
      feeScheduleManager
    );

    return treatments.map((chartedTreatment) => ({
      ...chartedTreatment,
      basePriceOverride: treatment.basePriceOverride,
      serviceCodes: this._addChartedSurfacesToServiceCodes(
        PricedServiceCodeEntry.duplicateCodes(treatment.pricedServiceCodes),
        chartedTreatment.chartedSurfaces
      ),
      serviceCodeGroups: treatment.serviceCodeGroups.map((group) => ({
        ...group,
        serviceCodes: this._addChartedSurfacesToServiceCodes(
          PricedServiceCodeEntry.duplicateCodes(group.serviceCodes),
          chartedTreatment.chartedSurfaces
        ),
      })),
      serviceCodeSmartGroups: treatment.serviceCodeSmartGroups.map((group) => ({
        ...group,
        serviceCodes: this._addChartedSurfacesToServiceCodes(
          PricedServiceCodeEntry.duplicateCodes(group.serviceCodes),
          chartedTreatment.chartedSurfaces
        ),
      })),
    }));
  }

  private async _getTreatmentAndSurface(
    treatment: WithRef<ITreatmentConfiguration>,
    quantity: number,
    feeSchedule: WithRef<IFeeSchedule>,
    selectedSurfaces: Partial<IChartedRef>[],
    feeScheduleManager: FeeScheduleManager
  ): Promise<ITreatmentAndSurface> {
    const calc = new ChartedItemTotalCalculator();
    const requiredCodes = TreatmentConfiguration.getCombinedServiceCodes(
      treatment,
      [ServiceCodeGroupType.Required]
    );
    const serviceCodes = requiredCodes.map((serviceCode) =>
      PricedServiceCodeEntry.applyFeeSchedule(
        serviceCode,
        requiredCodes,
        feeScheduleManager,
        false
      )
    );
    const serviceCodeGroups = TreatmentConfiguration.getServiceGroups(
      treatment,
      [ServiceCodeGroupType.Exclusive]
    );
    const groupCodes = flatten(
      serviceCodeGroups.map((group) => group.serviceCodes)
    );
    const pricedServiceCodeGroups = serviceCodeGroups.map(
      (serviceCodeGroup) => {
        return PricedServiceCodeGroup.applyFeeSchedule(
          serviceCodeGroup,
          groupCodes,
          feeScheduleManager,
          true,
          false
        );
      }
    );

    let disabled = false;
    const compatibleSurfaces =
      ChartableSurfaceResolver.getChartableSurfaces(treatment);

    if (
      isEqual(compatibleSurfaces, [ChartableSurface.Unscoped]) ||
      isEqual(compatibleSurfaces, [ChartableSurface.WholeMouth]) ||
      !compatibleSurfaces.length
    ) {
      disabled = true;
    }

    const chartingAs = await snapshot(this._chartingAs$);
    const chartedSurfaces: IChartedSurface[] = this._getChartedSurfaces(
      compatibleSurfaces,
      stafferToNamedDoc(chartingAs),
      treatment,
      selectedSurfaces
    );

    const serviceCodeSmartGroups =
      TreatmentConfiguration.getSmartGroups(treatment);

    const chartedTreatments = await ChartedTreatmentUpdater.syncPricingRules([
      ChartedTreatment.init({
        config: treatment,
        feeSchedule,
        scopeRef: {
          scope: ChartableSurface.Unscoped,
        },
        serviceCodes,
        serviceCodeGroups: pricedServiceCodeGroups,
        serviceCodeSmartGroups,
      }),
    ]);

    const chartedTreatment = first(chartedTreatments);
    const treatmentPrice = chartedTreatment
      ? calc.treatment(chartedTreatment)
      : 0;
    const treatmentWithSurfaces: ITreatmentAndSurface = {
      treatment,
      quantity,
      pricedServiceCodes: chartedTreatment
        ? chartedTreatment.serviceCodes
        : serviceCodes,
      serviceCodeGroups: chartedTreatment
        ? chartedTreatment.serviceCodeGroups
        : pricedServiceCodeGroups,
      serviceCodeSmartGroups: chartedTreatment
        ? chartedTreatment.serviceCodeSmartGroups
        : serviceCodeSmartGroups,
      price: disabled
        ? treatmentPrice
        : treatmentPrice * chartedSurfaces.length,
      chartedSurfaces,
      disabled,
    };

    return treatmentWithSurfaces;
  }

  private _getChartedSurfaces(
    compatibleSurfaces: ChartableSurface[],
    chartedBy: INamedDocument<IStaffer>,
    treatment: WithRef<ITreatmentConfiguration>,
    selectedSurfaces: Partial<IChartedRef>[]
  ): IChartedSurface[] {
    if (isEqual(compatibleSurfaces, [ChartableSurface.WholeMouth])) {
      return [
        ChartedSurface.init({
          chartedRef: {
            wholeMouth: true,
          },
          chartedBy,
        }),
      ];
    }

    if (
      isEqual(compatibleSurfaces, [ChartableSurface.Unscoped]) ||
      !compatibleSurfaces.length
    ) {
      return [
        ChartedSurface.init({
          chartedRef: {
            unscoped: true,
          },
          chartedBy,
        }),
      ];
    }

    if (!selectedSurfaces.length) {
      return [];
    }

    const chartedRefs = ChartableSurfaceResolver.getChartedRefs(
      treatment,
      selectedSurfaces
    );
    return chartedRefs.map((chartedRef) =>
      ChartedSurface.init({ chartedRef, chartedBy })
    );
  }

  private _addChartedSurfacesToServiceCodes(
    serviceCodes: IPricedServiceCodeEntry[],
    chartedSurfaces: IChartedSurface[]
  ): IPricedServiceCodeEntry[] {
    return serviceCodes.map((serviceCode) => ({
      ...serviceCode,
      chartedSurfaces,
    }));
  }
}
